import React, { useState } from 'react';

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Stack,
  TextField
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiErrorMessage } from '@/components/apiRequest';
import { useShellNotice, useSingleRoot } from '@/components/NewUI/SingleRoot';

import { FormTextField, MultilineFormTextField } from './FormTextField';
import { sharedClasses } from './sharedClasses';
import Api from '../../Application/API';
import { styles } from '../../Application/Modals/styles';
import { classes } from '../../Job/Applications/styles';
import { formatUserPermissions } from '../Utilities/formatUserPermissions';

import type { Dispatch, SetStateAction } from 'react';

import type { SnackbarSetter } from '@/components/NewUI/ComponentsLibrary';

import type { ApplicationAction } from '../../Application/types';
import type { ISmsTemplate } from '../../Job/Applications/Modals/SMSCandidates';

const LOAD_ERROR = 'Failed to load SMS templates';
const SAVE_ERROR = 'Failed to save SMS template';

const SmsTemplateComponent = ({
  smsMessage,
  setSmsMessage,
  smsTemplate,
  setSmsTemplate,
  setSnackbar,
  dispatch,
  dropdownLabel,
  smsMessageError,
  placeholders,
  multipleSms
}: {
  smsMessage: string;
  setSmsMessage: Dispatch<SetStateAction<string>>;
  smsTemplate: ISmsTemplate | null;
  setSmsTemplate: Dispatch<SetStateAction<ISmsTemplate | null>>;
  setSnackbar?: SnackbarSetter;
  dispatch?: Dispatch<ApplicationAction>;
  dropdownLabel?: string;
  smsMessageError?: string;
  placeholders?: string[];
  multipleSms?: boolean;
}) => {
  const [smsTemplateName, setSmsTemplateName] = useState<string>('');
  const [smsTemplateError, setSmsTemplateError] = useState<string>('');
  const queryClient = useQueryClient();
  const userPermissions = formatUserPermissions();
  const canSaveSmsTemplates = !!userPermissions?.Entities?.['Create / Edit / Delete SMS Templates'];

  const shell = useSingleRoot();
  const notify = useShellNotice(
    setSnackbar ?? ((payload) => dispatch?.({ type: 'SET_SNACKBAR', payload }))
  );

  const { data: smsTemplates, isFetching: fetchingSmsTemplates } = useQuery({
    queryKey: ['sms templates'],
    queryFn: async () => {
      const { res } = await Api.getSmsTemplates();
      return res.sms_templates;
    },
    meta: { errorFallback: LOAD_ERROR },
    onError: shell ? undefined : (error) => notify(apiErrorMessage(error, LOAD_ERROR), 'error'),
    initialData: []
  });

  const { mutate: saveTemplate, isLoading: savingTemplate } = useMutation({
    mutationFn: async () => {
      const { res } = await Api.saveSmsTemplate(smsTemplateName, smsMessage);
      return res;
    },
    meta: { errorFallback: SAVE_ERROR },
    onError: shell ? undefined : (error) => notify(apiErrorMessage(error, SAVE_ERROR), 'error'),
    onSuccess: (res) => {
      queryClient.setQueryData(['sms templates'], [...smsTemplates, res]);
      setSmsTemplate(res);
      setSmsTemplateName('');
      notify('Template saved', 'success');
    }
  });

  const handleSaveTemplate = () => {
    if (smsTemplates?.find((template: ISmsTemplate) => template.title === smsTemplateName)) {
      setSmsTemplateError('Template name already exists');
      return;
    }
    saveTemplate();
  };

  return (
    <Stack sx={{ rowGap: 3, paddingTop: 4 }}>
      <Autocomplete
        disablePortal
        getOptionLabel={(option) => option.title || ''}
        id="sms-template-select"
        loading={fetchingSmsTemplates}
        loadingText="Loading SMS templates..."
        options={smsTemplates}
        sx={{ ...sharedClasses.formAutocomplete, width: '100%' }}
        value={smsTemplate}
        onChange={(_event, value) => {
          if (value) {
            setSmsMessage(value.body);
          } else {
            setSmsMessage('');
          }
          setSmsTemplate(value);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={dropdownLabel?.length ? dropdownLabel : 'Choose SMS template'}
            sx={{ '& .MuiInputBase-root': { margin: 'unset', marginTop: 1 } }}
            slotProps={{
              ...params.slotProps,
              inputLabel: { shrink: true }
            }}
          />
        )}
        slotProps={{
          listbox: { style: classes.addToEventListboxProps }
        }}
      />
      <Stack sx={{ width: '100%' }}>
        <MultilineFormTextField
          required
          label="Content"
          maxLength={multipleSms ? undefined : 160}
          onChange={(e) => setSmsMessage(e.target.value)}
          value={smsMessage}
        />
        <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', columnGap: 2 }}>
          <Stack>
            {placeholders ? (
              <Stack
                sx={{ color: '#838383', fontSize: '11px', lineBreak: 'anywhere', paddingLeft: 0.5 }}
              >
                Available placeholders are: {placeholders.map((p) => `{${p}}`).join(', ')}
              </Stack>
            ) : null}
            <Stack sx={styles.errorMessage}>{smsMessageError}</Stack>
          </Stack>
          <Stack sx={classes.addToEventCharacterCount}>
            {multipleSms
              ? `${smsMessage.length} characters (${Math.ceil(smsMessage.length / 160)} messages)`
              : `${smsMessage.length} / 160`}
          </Stack>
        </Stack>
      </Stack>
      <Collapse
        in={canSaveSmsTemplates && !!smsMessage.trim() ? smsMessage !== smsTemplate?.body : null}
      >
        <Stack sx={{ flexDirection: 'row', alignItems: 'baseline', columnGap: 5 }}>
          <Stack>
            <FormTextField
              id="new-sms-template-title"
              label="Template title"
              value={smsTemplateName}
              onChange={(e) => {
                setSmsTemplateName(e.target.value);
                if (smsTemplateError) setSmsTemplateError('');
              }}
              styles={{
                width: '290px',
                '& .MuiInputBase-input': { margin: 'unset', marginTop: 1 }
              }}
            />
            {smsTemplateError ? (
              <Box sx={{ ...classes.saveSMSTemplateError, position: 'inherit' }}>
                {smsTemplateError}
              </Box>
            ) : null}
          </Stack>
          <Box sx={{ transform: 'translateY(-1.5px)' }}>
            <Button
              id="save-sms-template"
              onClick={handleSaveTemplate}
              sx={classes.saveSMSTemplateButton}
              type="submit"
            >
              {savingTemplate ? <CircularProgress color="inherit" size={20} /> : 'Save template'}
            </Button>
          </Box>
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default SmsTemplateComponent;
